import { createContext, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "src/api/hooks";
import {
  tasksApi,
  useAssignNextWaitingPatientMutation,
  useUpdateWaitingRoomPatientMutation,
} from "src/api/services/tasksApi";
import { MatchParam } from "src/constants/matches";

interface ContextType {
  getNextTask: () => void;
  completeAndNext: (id: number) => void;
}

export const TasksContext = createContext<ContextType>({
  getNextTask: () => {},
  completeAndNext: () => {},
});

interface Props {
  children: React.ReactNode;
}

const TasksProvider = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const [assignNextWaitingPatient] = useAssignNextWaitingPatientMutation();
  const [updatePatient] = useUpdateWaitingRoomPatientMutation();
  const { queue } = useParams<MatchParam<"QUEUE_TYPE">>();
  const navigate = useNavigate();

  const assign = useCallback(() => {
    if (queue == null) return;

    assignNextWaitingPatient()
      .unwrap()
      .then((response) => {
        if (response == null) {
          navigate("no-tasks");
          return;
        }

        dispatch(
          tasksApi.util.upsertQueryData(
            "getWaitingRoomPatient",
            response.id,
            response,
          ),
        );
        navigate(`${response.id}`);
      });
  }, [assignNextWaitingPatient, dispatch, navigate, queue]);

  const completeAndNext = useCallback(
    (id: number) => {
      updatePatient({
        id,
        status: "COMPLETED",
        completed_at: new Date().toISOString(),
      })
        .unwrap()
        .then(assign);
    },
    [assign, updatePatient],
  );

  const value = useMemo(
    () => ({
      getNextTask: assign,
      completeAndNext,
    }),
    [assign, completeAndNext],
  );

  return (
    <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
  );
};

export default TasksProvider;
